import store from "application/store/Store";
import {
  GoogleAnalyticsCategories,
  GoogleAnalyticsEvents,
  GoogleAnalyticsLabels,
} from "./ga.types";
import { sendEventToMetaCapiService } from "../metaCapi/meta-capi.service";
import SHA256 from "crypto-js/sha256";
import encHex from "crypto-js/enc-hex";
import { EVENTS_TO_META, IS_TESTEABLE } from "application/utils/constants";
import { v4 as uuidv4 } from "uuid";

type PossibleProducts = "tdc" | "ca" | "py" | "cs" | "pc" | undefined;
type PossibleAccountTypes = "plus" | "standard" | undefined;
type PossibleNationalities = "uruguay" | "foreign" | undefined;

const GA_ID = import.meta.env.VITE_ONBOARDING_ANALYTICS_ID;

const GA_ENABLED =
  window.location.href.includes("localhost") ||
  window.location.href.includes("misolicitud.santander");

const TRACKING_DEBUG =
  window.location.href.includes("debugGA") ||
  window.location.href.includes("debugTracking");

declare global {
  interface Window {
    dataLayer: any[];
    fbq?: (...args: any[]) => void;
  }
}

export class TrackerService {
  static product: PossibleProducts;
  static accountType: PossibleAccountTypes;
  static userNationality?: PossibleNationalities;
  static initialized = false;
  static debugGa = window.location.href.includes("localhost") || TRACKING_DEBUG;
  // ------------------- GA -------------------
  static setUserId(userId: string) {
    try {
      if (GA_ENABLED) ga("set", "userId", userId);
    } catch {}
  }
  static trackLandingDevice(params: {
    device: "desktop" | "mobile";
    fromQR: boolean;
    affinity?: string;
  }) {
    const { device, fromQR, affinity } = params;
    const deviceName =
      device === "desktop" ? "DESKTOP" : fromQR ? "MOBILE_QR" : "MOBILE";
    const eventName = `Landing_${deviceName}` as GoogleAnalyticsCategories;
    TrackerService.trackGA(eventName, affinity);
  }

  static initialize(isRetry = false) {
    setTimeout(() => {
      if (GA_ENABLED) {
        if (TrackerService.gaLoaded()) {
          ga("create", GA_ID, "auto");
          console.info("Google Analytics loaded properly");
        } else {
          TrackerService.initialized = false;

          if (!isRetry) {
            console.info("Google Analytics did not load properly");
          }
        }
      } else {
        if (TrackerService.debugGa) {
          console.info("Google Analytics not enabled for current host");
        }
      }
    }, 1000);
  }

  static gaLoaded() {
    return typeof ga === "function";
  }

  static setIsForeign(isForeign: boolean) {
    TrackerService.userNationality = isForeign ? "foreign" : "uruguay";
  }

  static getSalaryProduct() {
    let result = TrackerService.product;
    if (TrackerService.accountType) result += `_${TrackerService.accountType}`;
    if (TrackerService.userNationality)
      result += `_${TrackerService.userNationality}`;
    return result;
  }

  static getProduct() {
    const { isAhorro, isCredito, isPymes, isSalaryAccount, isCollegePack } =
      store.getState().SeleccionarProductoState;
    return isAhorro
      ? "ca"
      : isCredito
        ? "tdc"
        : isPymes
          ? "py"
          : isSalaryAccount
            ? "cs"
            : isCollegePack
              ? "pc"
              : undefined;
  }

  static getHasChip() {
    return store.getState().PrimerPasoState.conChip;
  }

  static getFormEventName() {
    const productType = TrackerService.getProduct();
    if (!productType) return "form";
    return `form_${productType}`;
  }

  static getFinalEventName() {
    const productType = TrackerService.getProduct();
    if (productType === undefined) {
      return `Paso17_FinalOnboarding_Unknown`;
    }

    if (productType === "ca" || productType === "py" || productType === "pc") {
      const accountInfo = store.getState().AccountInformationState?.accountType;
      if (!accountInfo) {
        return `Paso17_FinalOnboarding_NoAccountInfo`;
      }
      return `Paso17_FinalOnboarding_${accountInfo}`;
    }
    if (productType === "tdc") {
      const affinity =
        store.getState().PackOfferState?.packOffer?.affinityProgram?.name;
      const product = store.getState().PackOfferState?.packOffer?.product?.name;

      if (!affinity) {
        return `Paso17_FinalOnboarding_NoAffinity`;
      }
      return `Paso17_FinalOnboarding_${affinity}${product && product.trim() !== "" && product === "Platinum" ? `_${product}` : ""}`;
    }
    if (productType === "cs") {
      // const SalaryAccount = store.getState().PackOfferState?.packOffer?.SalaryAccountProgram?.name;
      const SalaryAccount = store.getState().SalaryAccountState?.salary;

      if (!SalaryAccount) {
        return `Paso17_FinalOnboarding_NoSalaryAccount`;
      }
      return `Paso17_FinalOnboarding_${SalaryAccount}`;
    }
  }

  static setProduct(
    product: PossibleProducts,
    accountTypeQueryParam?: "PLUS" | "STANDARD",
  ) {
    TrackerService.product = product;
    if (product === "cs" && accountTypeQueryParam) {
      TrackerService.accountType =
        accountTypeQueryParam === "PLUS"
          ? "plus"
          : accountTypeQueryParam === "STANDARD"
            ? "standard"
            : undefined;
    }
  }
  // ------------------- TRACKERS -------------------
  static async trackFinal() {
    const product = TrackerService.getProduct();
    if (!product) return;
    const eventName = TrackerService.getFinalEventName();
    await TrackerService.trackGA(eventName, `Final_${product.toUpperCase()}`);
  }
  static async trackDelivery(
    deliveryMethod: string,
    guardarDomicilio: boolean,
  ) {
    const eventName = `Paso13_CI_${
      TrackerService.getHasChip() ? "ConChip" : "SinChip"
    }_EnvioProducto`;
    await TrackerService.trackGA(eventName, deliveryMethod);
    if (guardarDomicilio) {
      await TrackerService.trackGA("DomicilioGuardado");
    }
  }
  // ------------------- GA + CAPI -------------------
  private static hashValue(value?: string): string | undefined {
    if (!value) return undefined;
    const normalized = value.trim().toLowerCase();
    return SHA256(normalized).toString(encHex);
  }
  private static buildUserData(raw: {
    em?: string;
    ph?: string;
    fn?: string;
    ln?: string;
    ge?: string;
    db?: string;
    ct?: string;
    st?: string;
    zp?: string;
    country?: string;
  }) {
    const hashed: Record<string, string> = {};
    for (const [key, val] of Object.entries(raw)) {
      if (val) hashed[key] = this.hashValue(val)!;
    }
    return hashed;
  }

  private static getCookie(name: string): string | undefined {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) {
      return parts.pop()?.split(";").shift();
    }
  }

  private static getMetaEventSuffix() {
    if (window.location.hostname === "localhost") return "_LOCALHOST";
    return IS_TESTEABLE ? "_PREPROD" : "";
  }

  static async trackGA(
    c?: GoogleAnalyticsCategories,
    l?: GoogleAnalyticsLabels,
    v?: string,
    metaEventCategory?: string,
  ) {
    try {
      const productEvent =
        TrackerService.getFormEventName() as GoogleAnalyticsEvents;
      if (!productEvent) return;

      // --- Google Analytics ---
      TrackerService.rawEvent(productEvent, c, l, v);
      TrackerService.debugEvent(productEvent, c, l, v);

      // --- Meta CAPI ---
      await TrackerService.sendMetaCapiEvent(
        productEvent,
        c,
        l,
        v,
        metaEventCategory,
      );
    } catch (err) {
      console.info("Error tracking events:", err);
    }
  }

  private static async sendMetaCapiEvent(
    productEvent: string,
    category?: string,
    label?: string,
    value?: string,
    metaEventCategory?: string,
  ) {
    if (!EVENTS_TO_META) {
      if (TrackerService.debugGa)
        console.debug("Meta CAPI disabled by EVENTS_TO_META flag");
      return;
    }
    try {
      const testName = TrackerService.getMetaEventSuffix();
      const eventName = category?.length
        ? `${category}${testName}`
        : label?.length
          ? `${label}${testName}`
          : value?.length
            ? `${value}${testName}`
            : "sin_dato";

      const eventTime = Math.floor(Date.now() / 1000);
      const eventId = uuidv4();

      const user = store.getState().TercerPasoState;
      const address = store.getState().QuintoPasoState;
      const onboardingstate = store.getState().onboardingState;
      const completeInitialUrl = onboardingstate.completeInitialUrl;

      let fb_login_id: string | undefined;
      if (typeof completeInitialUrl === "string" && completeInitialUrl) {
        try {
          const parsed = new URL(completeInitialUrl);
          fb_login_id = parsed.searchParams.get("fbclid") ?? undefined;
        } catch {
          const m = completeInitialUrl.match(/[?&]fbclid=([^&]+)/);
          if (m) fb_login_id = decodeURIComponent(m[1]);
        }
      }

      const user_data = {
        ...TrackerService.buildUserData({
          em: user.email,
          ph: user.phone,
          fn: user.name,
          ln: user.lastName,
          ge: user.sex,
          db: `${user.birthdayDay}/${user.birthdayMonth}/${user.birthdayYear}`,
          ct: user.nationality,
          country: "uy",
          st: address.departamento,
        }),
        client_user_agent: navigator.userAgent
          ? navigator.userAgent
          : undefined,
        fbp: TrackerService.getCookie("_fbp"),
        fbc: TrackerService.getCookie("_fbc"),
        ...(fb_login_id ? { fb_login_id } : {}),
      };

      const payload = {
        data: [
          {
            event_name: eventName,
            event_time: eventTime,
            event_id: eventId,
            user_data,
            ...(metaEventCategory
              ? { event_category: metaEventCategory }
              : {}),
          },
        ],
      };

      // Enviar a Meta CAPI
      await sendEventToMetaCapiService(payload);

      // Enviar al Pixel de Meta
      if (typeof window.fbq === "function") {
        window.fbq(
          "trackCustom",
          eventName,
          {
            category,
            label,
            value,
            ...(metaEventCategory
              ? { event_category: metaEventCategory }
              : {}),
          },
          { eventID: eventId },
        );
      } else {
        console.warn("Meta Pixel fbq is not available yet.");
      }

      if (TrackerService.debugGa)
        console.log("Meta CAPI & Pixel event sent:", payload);
    } catch (err) {
      console.error("Error sending Meta CAPI event:", err);
    }
  }

  static async sendMetaErrorEvent(
    eventName: string,
    error?: any,
    extraData?: Record<string, any>,
  ) {
    try {
      const testName = TrackerService.getMetaEventSuffix();
      const eventTime = Math.floor(Date.now() / 1000);
      const eventId = uuidv4();

      const user = store.getState().TercerPasoState;
      const address = store.getState().QuintoPasoState;

      const user_data = {
        ...TrackerService.buildUserData({
          em: user.email,
          ph: user.phone,
          fn: user.name,
          ln: user.lastName,
          ge: user.sex,
          db: `${user.birthdayDay}/${user.birthdayMonth}/${user.birthdayYear}`,
          ct: user.nationality,
          country: "uy",
          st: address.departamento,
        }),
        client_user_agent: navigator.userAgent || undefined,
        fbp: TrackerService.getCookie("_fbp"),
        fbc: TrackerService.getCookie("_fbc"),
      };

      const errorType = error?.response?.status
        ? `HTTP_${error.response.status}`
        : error?.name || "Unknown";
      const errorMessage =
        error?.response?.data?.message || error?.message || "No message";

      const custom_data = {
        error_type: errorType,
        error_message: errorMessage,
        ...(extraData || {}),
      };

      const payload = {
        data: [
          {
            event_name: `${eventName}${testName}`,
            event_time: eventTime,
            event_id: eventId,
            user_data,
            custom_data,
          },
        ],
      };

      // Enviar a Meta CAPI
      await sendEventToMetaCapiService(payload);

      // Enviar al Pixel (opcional)
      if (typeof window.fbq === "function") {
        window.fbq("trackCustom", `${eventName}${testName}`, custom_data, {
          eventID: eventId,
        });
      } else {
        console.warn("Meta Pixel fbq is not available yet.");
      }

      if (TrackerService.debugGa)
        console.log("Meta Error Event sent:", payload);
    } catch (err) {
      console.error("Error sending Meta Error Event:", err);
    }
  }

  static rawEvent(
    event: string,
    category: string,
    label: string,
    value: string,
  ) {
    if (typeof ga === "function") {
      ga("send", "event", event, category, label, value);
    } else if (this.debugGa) {
      console.log("GA not loaded");
    }
    if (window.dataLayer) {
      window.dataLayer.push({
        event,
        event_category: category,
        event_label: label,
        custom_value: value,
      });
    }
  }

  static debugEvent(e: string, c?: string, l?: string, v?: string) {
    this.debugGa &&
      console.log(
        `%ce: ${e}`,
        "color: #4CAF50; font-weight: bold",
        `Category: ${c}`,
        `Label: ${l}`,
        `Value: ${v}`,
      );
  }
}
